import { computed } from 'vue'
import { useViewAsStore, type ViewAsRole } from '~/stores/viewAs'
import { useTabSessionStore } from '~/stores/tabSession'

/**
 * "View as" — display-role override for admins (and advisors, limited to
 * Client). The whole SPA gates visibility through `hasDisplayRole` / the
 * `display*` computeds below instead of the raw `useAuth().hasRole`, so when a
 * preview is active the chrome renders as the lower role. The REAL role
 * (useAuth) is left untouched so backend calls still carry true privileges.
 *
 * Safety: only a real Admin/Advisor can start a preview, and previewing is a
 * strict DOWNGRADE (Client/Advisor/Rep ⊂ Admin's access), so it can only hide
 * UI — never escalate. The backend independently authorizes every request by
 * the real token + ACL (AllowedHouseholds2Async).
 */
export function useViewAs() {
  const store = useViewAsStore()
  const { me, hasRole } = useAuth()
  const tab = useTabSessionStore()

  const realIsAdmin = computed(() =>
    hasRole('Admin') || hasRole('Admin.Read') || hasRole('Admin.Write'))
  const realIsAdvisor = computed(() => hasRole('Advisor'))
  /** Who may start a preview at all. */
  const canViewAs = computed(() => realIsAdmin.value || realIsAdvisor.value)

  // A persisted preview is only honored if the real user is actually allowed to
  // preview (defends against a stale sessionStorage entry on a downgraded user).
  const active = computed(() => store.active && canViewAs.value)
  const role = computed<ViewAsRole | null>(() => (active.value ? store.role : null))
  const householdName = computed(() => store.householdName)
  const repName = computed(() => store.repName)
  /** The household a "view as Client" preview is scoped to (drives display-access). */
  const displayHouseholdId = computed<number | null>(() =>
    active.value && store.role === 'Client' ? store.householdId : null)
  /** The representative a "view as Representative" preview is scoped to (filters the household list). */
  const displayRepId = computed<number | null>(() =>
    active.value && store.role === 'Representative' ? store.repId : null)

  // Display roles: the override when previewing, else the real roles. The whole
  // SPA's role-gating reads THIS (not useAuth().roles) for visibility.
  const displayRoles = computed<string[]>(() =>
    active.value && store.role ? [store.role] : (me.value.roles || []))
  const hasDisplayRole = (r: string) => displayRoles.value.includes(r)
  const hasAnyDisplayRole = (rs: string[]) => rs.some(hasDisplayRole)
  const displayIsAdmin = computed(() =>
    hasDisplayRole('Admin') || hasDisplayRole('Admin.Read') || hasDisplayRole('Admin.Write'))

  /** Start previewing a household AS its client (admin or advisor). */
  function startClient(householdId: number, householdName?: string | null) {
    if (!canViewAs.value || !householdId) return
    store.set('Client', { householdId, householdName })
    // Scope the whole app to that household + drop the client into their portal.
    tab.setHouseholdId(householdId)
    return navigateTo('/household/overview')
  }

  /** Admin-only: preview the generic Advisor chrome (no single advisor identity
   *  to scope to). */
  function startRole(r: ViewAsRole) {
    if (!realIsAdmin.value) return
    store.set(r)
    return navigateTo(r === 'Client' ? '/household/overview' : '/advisor')
  }

  /** Admin-only: preview as a SPECIFIC representative — scopes the household
   *  list to that rep (useHouseholdPicker reads displayRepId). */
  function startRep(repId: number, repName?: string | null) {
    if (!realIsAdmin.value || !repId) return
    store.set('Representative', { repId, repName })
    return navigateTo('/advisor')
  }

  /** Leave the preview and return to where the real role belongs. */
  function exit() {
    store.clear()
    return navigateTo(realIsAdmin.value ? '/admin/users' : '/advisor')
  }

  return {
    active, role, householdName, repName, displayHouseholdId, displayRepId,
    canViewAs, realIsAdmin, realIsAdvisor,
    displayRoles, hasDisplayRole, hasAnyDisplayRole, displayIsAdmin,
    startClient, startRole, startRep, exit
  }
}
